import fromPairs from 'lodash/fromPairs';
import parseUISchemaFromSchema from './parseUISchemaFromSchema';
import parseObjectMapFromSchema from './parseObjectMapFromSchema';
import transformJsonSchema from './transformJsonSchema';
import { invertMap, ObjectMap } from './mapObject';
import { UWEEntity } from '../api/types';
import { FormFieldComponent } from '../components/FormComponentTypes';
import { JSONSchema, JSONSchemaObject } from './jsonschemaTypes';
import collectEmbeddedUIFieldsFromUISchema from './collectEmbeddedUIFieldsFromUISchema';

export type FormDefinition = {
  commentFieldsMap?: unknown;
  customFields?: Record<string, FormFieldComponent<any>>;
  displayName?: unknown;
  name?: unknown;
  objectMap?: ObjectMap;
  objectSchema?: string;
  objectSchemaId?: number;
  options?: {
    alignButtons?: unknown;
    ignoreVisibilityOnValidate?: unknown;
    readonly?: unknown;
    submitButtons?: unknown;
  };
  page?: unknown;
  rootSchema?: JSONSchema;
  schema?: JSONSchemaObject['properties'];
  schemaProps?: Partial<JSONSchema>;
  title?: unknown;
  uiSchema?: unknown;
};

function parseFormDefinition(formDefinition: FormDefinition, currentEntity?: UWEEntity) {
  if (!formDefinition) return null;

  const schema = transformJsonSchema(
    (formDefinition.rootSchema ?? {
      type: 'object',
      properties: formDefinition.schema ?? {},
      ...formDefinition.schemaProps,
    }) as JSONSchema,
    currentEntity
  );

  const parsedUiSchema = parseUISchemaFromSchema(schema, { 'ui:field': 'HideObjectField' });
  const uiSchema = Object.keys(formDefinition.uiSchema || {}).length
    ? {
        ...(formDefinition.uiSchema ?? {}),
        ...parsedUiSchema,
      }
    : parsedUiSchema;

  const objectMap = parseObjectMapFromSchema(schema, formDefinition.objectMap, {
    removeNullMappedFields: true,
  });

  const embeddedFields = collectEmbeddedUIFieldsFromUISchema(uiSchema);
  let customFields: Record<string, FormFieldComponent<any>> | undefined = { ...formDefinition.customFields, ...embeddedFields };
  if (Object.values(customFields).length == 0) customFields = undefined;

  const objectMapAsMap = fromPairs(objectMap as Array<[string, any]>);
  const invObjectMap = objectMap ? invertMap(objectMap) : null;
  const invObjectMapAsMap = fromPairs(invObjectMap as Array<[string, any]>);

  return {
    entityType: formDefinition.objectSchema || { id: formDefinition.objectSchemaId },
    page: formDefinition.page ?? formDefinition.name,
    commentFieldsMap: formDefinition.commentFieldsMap,
    displayName: formDefinition.page ?? formDefinition.name,
    title: formDefinition.title,
    name: formDefinition.name,
    schema,
    uiSchema,
    customFields,
    objectMap,
    objectMapAsMap,
    invObjectMap,
    invObjectMapAsMap,
    submitButtons: formDefinition.options?.submitButtons,
    alignButtons: formDefinition.options?.alignButtons,
    readonly: formDefinition.options?.readonly,
    ignoreVisibilityOnValidate: formDefinition.options?.ignoreVisibilityOnValidate,
  };
}

export default parseFormDefinition;
